/**
 * 68.39b (DECISIONS 196): reworked into an artillery strike — a circle at the point called, n rounds aimed at seeded points
 * inside it, fired from an off-board battery behind it and flown as rounds by the bullets' step (a person-scale lob,
 * AIRSTRIKE.fall: `solveStrikeRound`), each bursting with the bomb's class where it lands. The rest of this header is 68.39's
 * call, kept (its stick of bombs along the line is gone).
 *
 * 68.39 (decision 188 (5), the first commander capability; the owner: "some sort of n-use consumible airstrike that we can
 * give to commanders to use throughout the game, can just mock origination point to \"homebase\" for now"): a side's
 * commander holds N airstrikes for the match (MatchSetup.airstrikesByTeam). A call at a point spends one and queues it: it
 * comes down AIRSTRIKE.prepSeconds later plus the flight from the side's home base (`airstrikeHome`) at
 * AIRSTRIKE.speedPeople — no aircraft or crew modelled — as a stick of AIRSTRIKE.bombs person-family bursts along the line
 * from the base, centred on the aim scattered by the fix's quality: what the side's picture holds round the point
 * (core/picture.ts) — a known enemy there seen just now, a fainter belief by its confidence, nothing (a guess is a guess).
 * Pure and deterministic (a seeded draw per strike); the Match runs the bursts on the rocket's blast (core/rocket.ts).
 */
import { explosionBlast } from './explosions';
import type { AirstrikeInbound, GameState, LayPass, PlanetState, StrikeFlight, StrikeLaying, Vec3 } from '../types';
import { AIRSTRIKE, EXPLOSIONS, PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { GUN_CLASSES, MORTAR, TOWED_GUNS, ACTION_WIND } from '../constants/action';
import type { StructureState } from '../types';
import { structureTeam } from './body';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import type { StrikeRound } from '../types';
import { gravityAccel } from './physics';
import { G } from '../constants/physics';
import { predictShot } from './aimSolve';
import { dacos, dcos, dsin, dlog } from './dmath';
import { SeededRandom } from './random';
import { personSetOf, type PersonSet } from './scaleProfile';
import { groundCeiling, groundCeilingNear, personPointOn, personSurfaceRadius, warmGroundCeiling } from './fineTerrain';
import { teamPicture } from './picture';
import { baseStoreSpot } from './buildCosts';
import { actionWindAccelAt } from './wind';
import { windShare } from './weapons';
import { baseShells, spendBaseShells } from './munitions';
import { towedGunsOn } from './defaultOn';

/** The airstrike's burst at this scale (world units: the blast convention's radius, its damage circle 2.2 × it). */
export function airstrikeBlast(state: Pick<GameState, 'config'>): { radius: number; damage: number } {
  return explosionBlast(state, AIRSTRIKE.explosion)!;   // 65.35: its class's
}

/** The salvo's size: its rounds (AIRSTRIKE.rounds, or the match's). */
export function salvoSize(state: Pick<GameState, 'config'>): number {
  return Math.max(1, Math.min(12, Math.floor(state.config.airstrikeRounds ?? AIRSTRIKE.rounds)));
}

/**
 * How many a side has left (0 with none held). 69.30 step 3 (GameConfig.munitionsSupply, DECISIONS 248 (3)): for a side that
 * holds strikes, the whole salvos its base's light shells pay for (core/munitions.ts `baseShells`) — the count retired.
 */
export function airstrikesLeft(state: Pick<GameState, 'airstrikes' | 'config' | 'structures'>, team: number): number {
  const held = state.airstrikes?.left[team];
  if (held === undefined) return 0;
  return state.config.munitionsSupply ? Math.floor(baseShells(state, team) / salvoSize(state)) : held;
}

/** The side's state with each side's count (absent when no side holds any: nothing changes). */
export function withAirstrikes(state: GameState, byTeam: readonly number[] | undefined): GameState {
  if (!byTeam?.some(n => n > 0)) return state;
  const left: Record<number, number> = {};
  byTeam.forEach((n, t) => { if (n > 0) left[t] = Math.floor(n); });
  return { ...state, airstrikes: { left, issued: { ...left }, inbound: [], next: 0 } };
}

/**
 * The fix the side has on a point, from its picture: a known enemy within AIRSTRIKE.fixPeople seen in the last
 * AIRSTRIKE.seenSeconds is `seen` (the tightest scatter); a fainter belief there scatters by its confidence toward the
 * picture's; nothing known there is a guess.
 */
export function airstrikeFix(state: GameState, team: number, planetIndex: number, dir: Vec3, tick: number): { fix: AirstrikeInbound['fix']; scatterPeople: number } {
  const S = AIRSTRIKE.scatterPeople, planet = state.planets[planetIndex];
  if (!planet) return { fix: 'guess', scatterPeople: S.guess };
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, d = vecNormalize(dir);
  let best: { confidence: number; tick: number } | null = null;
  for (const b of teamPicture(state, team, tick)) {
    if (b.planetIndex !== planetIndex) continue;
    if (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(b.surfaceDir), d)))) * planet.radius > AIRSTRIKE.fixPeople * person) continue;
    if (!best || b.confidence > best.confidence) best = { confidence: b.confidence, tick: b.tick };
  }
  if (!best) return { fix: 'guess', scatterPeople: S.guess };
  if ((tick - best.tick) * PHYS_DT <= AIRSTRIKE.seenSeconds) return { fix: 'seen', scatterPeople: S.seen };
  return { fix: 'picture', scatterPeople: S.seen + (S.picture - S.seen) * (1 - Math.max(0, Math.min(1, best.confidence))) };
}

/**
 * Where a side's airstrikes come from: its home base (its HQ, the board's base or its landing: `baseStoreSpot`), else — a
 * board with no base, the plain skirmish — where its people stand (their centre on the planet most of them are on).
 */
export function airstrikeHome(state: GameState, team: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const base = baseStoreSpot(state, team);
  if (base) return base;
  const mine = state.players.filter(p => p.teamIndex === team);
  if (!mine.length) return null;
  const counts = new Map<number, number>();
  for (const p of mine) counts.set(p.planetIndex, (counts.get(p.planetIndex) ?? 0) + 1);
  const pi = [...counts].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
  const sum = mine.filter(p => p.planetIndex === pi).reduce((s, p) => vecAdd(s, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 });
  return vecLength(sum) > 1e-9 ? { planetIndex: pi, surfaceDir: vecNormalize(sum) } : { planetIndex: pi, surfaceDir: mine[0].surfaceDir };
}

/** A unit tangent at `dir` pointing away from `from` (the stick's line, from the base toward the aim); any tangent when they coincide. */
export function lineFrom(dir: Vec3, from: Vec3 | null): Vec3 {
  const d = vecNormalize(dir);
  const raw = from ? vecSub(d, vecNormalize(from)) : { x: 1, y: 0, z: 0 };
  let t = vecSub(raw, vecScale(d, vecDot(raw, d)));
  if (vecLength(t) < 1e-9) { const any = Math.abs(d.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }; t = vecSub(any, vecScale(d, vecDot(any, d))); }
  return vecNormalize(t);
}

/** The direction `dist` world units from `dir` along the tangent `t` on a planet of `radius`. */
export function along(dir: Vec3, t: Vec3, dist: number, radius: number): Vec3 {
  const a = dist / radius;
  return vecNormalize(vecAdd(vecScale(vecNormalize(dir), dcos(a)), vecScale(t, dsin(a))));
}

/**
 * 68.39b: the flight of a round fired from `from` (a surface direction) at `toward` (another) on `planet`: launched half a
 * person up at AIRSTRIKE.elevation on the bearing to it, a person-scale lob (`AIRSTRIKE.fall` of the felt gravity: the 40 mm's
 * flight, core/aimSolve.ts predictShot — the sim's own step for a round), its speed found on that flight by the secant method
 * on where it lands (a few passes, once at the call). Not the planet family's step (the shells'): at k 1/20 its gravity, some
 * 8,800 people a second², made a 300-person arc a 0.27 s blink that landed ±20 people off by the tick. Null: none lands it.
 */
export function solveStrikeRound(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, from: Vec3, toward: Vec3, wind: StrikeWind = null): { pos: Vec3; vel: Vec3; seconds: number } | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const pos = personPointOn(planet, vecNormalize(from), ps, 0.5 * person), up = vecNormalize(vecSub(pos, planet.position));
  const target = personPointOn(planet, vecNormalize(toward), ps, 0), rel = vecSub(target, pos);
  const flat = vecSub(rel, vecScale(up, vecDot(rel, up)));
  if (vecLength(flat) < 1e-9) return null;
  const e = AIRSTRIKE.elevation, dir = vecNormalize(vecAdd(vecScale(vecNormalize(flat), dcos(e)), vecScale(up, dsin(e))));
  const arc = (d: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(from), vecNormalize(d))))) * planet.radius;
  const want = arc(toward);
  const ground = groundTest(planet, ps);
  const fly = (v: number): { arc: number; seconds: number } | null => {
    const shot = predictShot(pos, vecScale(dir, v), { life: 30, fall: AIRSTRIKE.fall, gravityScale: ps.gravityScale, planets: state.planets, blocked: ground, stepLength: 0.5 * person, maxSteps: 20000, ...(wind ? { wind } : {}) });
    return shot.hit ? { arc: arc(vecSub(shot.at, planet.position)), seconds: shot.t } : null;
  };
  const g = vecLength(gravityAccel(pos, state.planets as never)) * AIRSTRIKE.fall * ps.gravityScale;
  let v0 = Math.sqrt(Math.max(1e-12, (want * g) / dsin(2 * e))), v1 = v0 * 1.05;
  let f0 = fly(v0), f1 = fly(v1);
  for (let k = 0; k < 10 && f0 && f1; k++) {
    if (Math.abs(f1.arc - want) <= 0.25 * person) break;
    const slope = (f1.arc - f0.arc) / (v1 - v0);
    if (!(Math.abs(slope) > 1e-12)) break;
    const v2 = Math.max(1e-9, v1 + (want - f1.arc) / slope);
    v0 = v1; f0 = f1; v1 = v2; f1 = fly(v1);
  }
  return f1 ? { pos, vel: vecScale(dir, v1), seconds: f1.seconds } : null;
}

/**
 * A flight's ground test on `planet` (predictShot's `blocked`): at or under the person's ground. 66.23 (the owner's URL: a
 * commander's strike laid by the towed guns was ~200 ms, 348,000 ground lookups — its lays' trial flights asked the ground
 * at every step, high over it the most of the way): above the ground's ceiling (fineTerrain groundCeiling) no lookup — the
 * same answer, the same arithmetic as the vector helpers' below it (the same bits), and nothing made there a step.
 */
function groundTest(planet: PlanetState, ps: PersonSet): (p: Vec3) => boolean {
  const ceil = groundCeiling(planet, ps), near = groundCeilingNear(planet, ps), P = planet.position;
  return p => {
    const x = p.x - P.x, y = p.y - P.y, z = p.z - P.z, d = Math.sqrt(x * x + y * y + z * z);
    if (d > ceil || d > near(x, y, z)) return false;   // (69.67: over the ground under it too — a megaplanet's mountains stand far over a board)
    const s = 1 / d;
    return d <= personSurfaceRadius(planet, { x: x * s, y: y * s, z: z * s }, ps);
  };
}

/**
 * 69.13 (b) (DECISIONS 232): a gun's lay — its muzzle speed fixed (`speedPeople`), the elevation solved from where it stands
 * (`from`) to `toward`. The round leaves the muzzle: the trunnion `trunnionPeople` up, the barrel `barrelPeople` along the lay.
 * Elevations are scanned (88° down to 3°, flown at a coarse step) for where the landing passes the target's distance, then
 * that bracket is refined (regula falsi, Illinois, at the fine step) to a quarter person: the high solution the first crossing
 * from above, the low the first from below. `near` (an elevation found for a nearby aim: the wind's passes, the next gun
 * abreast) brackets ±2°, then ±6°, round it first. Null: out of reach (no elevation lands that far), or no low solution under 3°.
 * Deterministic (dmath, fixed counts); flown as the strike's rounds fly (predictShot, AIRSTRIKE.fall).
 */
export function solveLay(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, from: Vec3, toward: Vec3, speedPeople: number,
  opts: { solution?: 'high' | 'low'; trunnionPeople?: number; barrelPeople?: number; near?: number; wind?: StrikeWind; fall?: number } = {}): { pos: Vec3; vel: Vec3; seconds: number; elevation: number } | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const trunnion = personPointOn(planet, vecNormalize(from), ps, (opts.trunnionPeople ?? TOWED_GUNS.trunnionPeople) * person), up = vecNormalize(vecSub(trunnion, planet.position));
  const target = personPointOn(planet, vecNormalize(toward), ps, 0), rel = vecSub(target, trunnion);
  const flat = vecSub(rel, vecScale(up, vecDot(rel, up)));
  if (vecLength(flat) < 1e-9) return null;
  const fwd = vecNormalize(flat), speed = speedPeople * person, barrel = (opts.barrelPeople ?? TOWED_GUNS.barrelPeople) * person;
  const stepPeople = layStepPeople(speedPeople);   // (69.64: a tick's travel at its speed, never under the lay's step)
  const f0 = vecNormalize(from), arc = (d: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(f0, vecNormalize(d))))) * planet.radius;
  const want = arc(toward), close = 0.25 * person;
  const ground = groundTest(planet, ps);
  const fly = (e: number, coarse = false): { arc: number; pos: Vec3; vel: Vec3; seconds: number; elevation: number } => {
    const dir = vecNormalize(vecAdd(vecScale(fwd, dcos(e)), vecScale(up, dsin(e)))), pos = vecAdd(trunnion, vecScale(dir, barrel)), vel = vecScale(dir, speed);
    const shot = predictShot(pos, vel, { life: 40, fall: opts.fall ?? AIRSTRIKE.fall, gravityScale: ps.gravityScale, planets: state.planets, blocked: ground, stepLength: (coarse ? 2 * stepPeople : stepPeople) * person, maxSteps: 40000, ...(opts.wind ? { wind: opts.wind } : {}) });
    return { arc: shot.hit ? arc(vecSub(shot.at, planet.position)) : -1, pos, vel, seconds: shot.t, elevation: e };
  };
  const high = (opts.solution ?? 'high') === 'high', deg = Math.PI / 180;
  type Flight = ReturnType<typeof fly>;
  // `short` lands short of the target, `long` at or past it: refined on the elevation between them (Illinois), the closest kept
  const refine = (short: Flight, long: Flight): Flight | null => {
    if (!(short.arc < want && long.arc >= want)) return null;
    let a = short, b = long, fa = a.arc - want, fb = b.arc - want, side = 0;
    let best = -fa < fb ? a : b;
    for (let k = 0; k < 12 && Math.abs(best.arc - want) > close; k++) {
      const c = fly((a.elevation * fb - b.elevation * fa) / (fb - fa)), fc = c.arc - want;
      if (c.arc < 0) break;
      if (Math.abs(fc) < Math.abs(best.arc - want)) best = c;
      if (fc >= 0) { b = c; fb = fc; if (side === -1) fa /= 2; side = -1; } else { a = c; fa = fc; if (side === 1) fb /= 2; side = 1; }
    }
    return best;
  };
  const done = (f: Flight) => ({ pos: f.pos, vel: f.vel, seconds: f.seconds, elevation: f.elevation });
  const bracket = (steeper: number, flatter: number) => { const s = fly(steeper), f = fly(flatter); return high ? refine(s, f) : refine(f, s); };   // (high: the steeper lands shorter; low: the flatter)
  if (opts.near !== undefined) {
    for (const w of [2, 6]) { const f = bracket(opts.near + w * deg, opts.near - w * deg); if (f) return done(f); }
  }
  const scan = [88, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 3].map(d => d * deg);
  if (!high) scan.reverse();
  let prev: number | null = null;
  for (const e of scan) {
    if (fly(e, true).arc >= want) {
      if (prev === null) return high ? done(fly(e)) : null;   // (high, at 88° already: that close, the steepest)
      const f = high ? bracket(prev, e) : bracket(e, prev);
      if (f) return done(f);
      // (the coarse flights' bracket missed at the fine step: widen it a step each way)
      const g = high ? bracket(prev + 5 * deg, e - 5 * deg) : bracket(e + 5 * deg, prev - 5 * deg);
      return g ? done(g) : null;
    }
    prev = e;
  }
  return null;
}

/**
 * 69.64: the step a gun's lay is flown at (people) — its round's travel in a tick at `speedPeople`, never under
 * TOWED_GUNS.layStepPeople (the light gun's 220 people a second and the mortar's 165 kept theirs; the 69.64 charges' rounds
 * fly 450–960, and a flight at the old step cost 2–4× the call).
 */
export function layStepPeople(speedPeople: number): number {
  return Math.max(TOWED_GUNS.layStepPeople, speedPeople * PHYS_DT);
}

/** 69.13 (b): a side's towed guns standing on a planet (its strike's battery), by id. */
export function strikeGuns(state: Pick<GameState, 'structures' | 'players'>, team: number, planetIndex: number): StructureState[] {
  return state.structures.filter(s => s.type === 'towedGun' && s.health > 0 && s.planetIndex === planetIndex && structureTeam(state, s) === team).sort((a, b) => a.id - b.id);
}

const reachMemo = new Map<string, number>();
/**
 * 69.13 (b): how far a gun class lands on a planet (people) — the furthest of an elevation scan (15–60° by 1.5°) fired from
 * level ground (the planet's sphere) at its speed; the felt gravity is the world's, so a class's reach is too. Memoised on
 * the planets' sizes, masses and places and the scale (a pure function of them). 69.64: `groundRadius` the level ground's
 * (a gun's own footing: near orbital speed, the 69.64 class's, a gun 170 people up the relief reaches twice as far as one
 * on the base sphere); absent, the planet's radius.
 */
export function gunReachPeople(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, speedPeople?: number, fall: number = GUN_CLASSES[TOWED_GUNS.gunClass].fall, groundRadius?: number): number {
  const key = reachKey(state, planetIndex, speedPeople, fall, groundRadius);
  if (key === null) return 0;
  const memo = reachMemo.get(key);
  if (memo !== undefined) return memo;
  const best = gunReachScan(state, planetIndex, speedPeople, fall, groundRadius);
  if (reachMemo.size > 256) reachMemo.clear();   // (69.64: a reach a charge and a gun's footing)
  reachMemo.set(key, best);
  return best;
}

/** gunReachPeople's memo key (null: no such planet) — its arguments, the scale and every planet's size, mass and place. */
function reachKey(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, speedPeople: number | undefined, fall: number, groundRadius?: number): string | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  if (speedPeople === undefined) { const c = gunCharges(state, planetIndex); speedPeople = c[c.length - 1]; }
  const ps = personSetOf(state), level = groundRadius ?? planet.radius;
  return `${planetIndex}|${speedPeople}|${fall}|${level}|${ps.k}|${ps.gravityScale}|${state.planets.map(p => `${p.radius},${p.mass},${p.position.x},${p.position.y},${p.position.z}`).join(';')}`;
}

/** Whether gunReachPeople holds this reach already (warmStrikes' check: the keys a strike asks are the ones built). */
export function gunReachMemoised(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, speedPeople?: number, fall: number = GUN_CLASSES[TOWED_GUNS.gunClass].fall, groundRadius?: number): boolean {
  const key = reachKey(state, planetIndex, speedPeople, fall, groundRadius);
  return key !== null && reachMemo.has(key);
}

/** gunReachPeople's scan, unmemoised (its value cold). */
export function gunReachScan(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, speedPeople?: number, fall: number = GUN_CLASSES[TOWED_GUNS.gunClass].fall, groundRadius?: number): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  if (speedPeople === undefined) { const c = gunCharges(state, planetIndex); speedPeople = c[c.length - 1]; }   // (69.77: the planet's top charge)
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, level = groundRadius ?? planet.radius;
  const up = { x: 0, y: 1, z: 0 }, way = { x: 1, y: 0, z: 0 }, from = vecAdd(planet.position, vecScale(up, level + TOWED_GUNS.trunnionPeople * person));
  const ground = (p: Vec3) => vecLength(vecSub(p, planet.position)) <= level;
  const arcOf = (d: number): number => {
    const e = (d * Math.PI) / 180, dir = vecNormalize(vecAdd(vecScale(way, dcos(e)), vecScale(up, dsin(e))));
    const shot = predictShot(vecAdd(from, vecScale(dir, TOWED_GUNS.barrelPeople * person)), vecScale(dir, speedPeople * person), { life: 40, fall, gravityScale: ps.gravityScale, planets: state.planets, blocked: ground, stepLength: Math.max(1, speedPeople * PHYS_DT) * person, maxSteps: 20000 });   // (69.64: a tick's travel, the old person a step at the light gun's 220)
    return shot.hit ? dacos(Math.max(-1, Math.min(1, vecDot(up, vecNormalize(vecSub(shot.at, planet.position)))))) * planet.radius / person : 0;   // (on the planet's ruler: the arcs every call measures)
  };
  // 69.64: a scan by 5° from 15° to 60°, then a golden-section search round the best (17 flights, where 31 at 1.5° were; a
  // reach is the pre-filter and the charge's pick, the solve decides)
  let best = 0, at = 15;
  for (let d = 15; d <= 60; d += 5) { const r = arcOf(d); if (r > best) { best = r; at = d; } }
  const G = 0.6180339887498949;
  let lo = Math.max(15, at - 5), hi = Math.min(60, at + 5), x1 = hi - G * (hi - lo), x2 = lo + G * (hi - lo), f1 = arcOf(x1), f2 = arcOf(x2);
  for (let k = 0; k < 5; k++) {
    if (f1 >= f2) { hi = x2; x2 = x1; f2 = f1; x1 = hi - G * (hi - lo); f1 = arcOf(x1); }
    else { lo = x1; x1 = x2; f1 = f2; x2 = lo + G * (hi - lo); f2 = arcOf(x2); }
  }
  best = Math.max(best, f1, f2);
  return best;
}

/**
 * The slow ticks (sweeps/2026-11-03-slow-ticks.md): what a side's first strike asks, built at setup once every structure is
 * placed (match/setup.ts, after the planner, the yards and the carve) — each planet's ground ceiling (fineTerrain
 * warmGroundCeiling: its mesh scan and tables, 5 ms on mega25) and, with the guns firing, each standing towed gun's reach at
 * its footing at each of its planet's charges (the keys the reach tests and gunCharge's search ask: the top charge's scan
 * 4–12 ms a gun, a side's three to six 16–45 ms on its strike's tick, the lower charges 2–11 ms on the next). Memos only, the
 * same answers.
 */
export function warmStrikes(state: GameState): void {
  if (!state.airstrikes) return;
  for (const p of state.planets) warmGroundCeiling(p);
  if (!gunsFire(state)) return;
  const ps = personSetOf(state), fall = GUN_CLASSES[TOWED_GUNS.gunClass].fall;
  for (const g of state.structures) {
    const planet = state.planets[g.planetIndex];
    if (g.type !== 'towedGun' || g.health <= 0 || !planet) continue;
    const level = personSurfaceRadius(planet, vecNormalize(g.surfaceDir), ps);
    for (const c of gunCharges(state, g.planetIndex)) gunReachPeople(state, g.planetIndex, c, fall, level);
  }
}

/**
 * 69.77: a gun class's charges (muzzle speeds, people a second) on a planet — GUN_CLASSES' own, each scaled down by one factor
 * when the top passes `topCircular` × the round's circular speed at the planet's surface (its own gravity × the person set's
 * gravityScale × the class's `fall`): past escape a round leaves the planet (the Story's lost every round, so no gun reached
 * anything). The owner's board's are the class's own. A pure function of the planet's mass and radius and the scale.
 */
export function gunCharges(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): readonly number[] {
  const cls = GUN_CLASSES[TOWED_GUNS.gunClass], planet = state.planets[planetIndex];
  if (!planet) return cls.charges;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const circular = Math.sqrt(cls.fall * ps.gravityScale * G * planet.mass / planet.radius) / person, top = cls.charges[cls.charges.length - 1];
  const cap = cls.topCircular * circular;
  return top <= cap ? cls.charges : cls.charges.map(c => c * cap / top);
}

/**
 * 69.64 (DECISIONS 288): the charge a gun fires at a point — the lowest of its class's charges (GUN_CLASSES: muzzle speeds)
 * whose reach from level ground at the gun's footing (gunReachPeople) passes the point's range by a margin (the target's
 * ground, the wind's passes); the top charge when none does (the solve decides).
 */
export function gunCharge(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, gun: Pick<StructureState, 'surfaceDir'>, toward: Vec3): number {
  const planet = state.planets[planetIndex], cls = GUN_CLASSES[TOWED_GUNS.gunClass], charges = gunCharges(state, planetIndex);   // (69.77: the planet's)
  if (!planet) return charges.length - 1;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, g = vecNormalize(gun.surfaceDir);
  const range = dacos(Math.max(-1, Math.min(1, vecDot(g, vecNormalize(toward))))) * planet.radius / person, level = personSurfaceRadius(planet, g, ps);
  const need = Math.min(range * 1.03 + 10, 0.995 * Math.PI * planet.radius / person);   // (no reach passes half the way round)
  // (a binary search: a charge's reach grows with its speed — three tables at most, where a scan built up to six)
  let lo = 0, hi = charges.length - 1;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (gunReachPeople(state, planetIndex, charges[mid], cls.fall, level) >= need) hi = mid; else lo = mid + 1; }
  return lo;
}

/**
 * 69.13 (a): whether a side's strikes are fired by its towed guns — the switch on (with the point defence) and the board's
 * bases standing, where the setup places them (DECISIONS 300: on by default, so a match with no board's bases keeps the
 * off-board battery, as the guns' switch off).
 */
export function gunsFire(state: Pick<GameState, 'config' | 'board'>): boolean {
  return towedGunsOn(state.config) && (state.board?.bases.length ?? 0) > 0;
}

/** 69.13 (b): whether a point is within a side's guns' reach on level ground (gunReachPeople: the bots' pre-filter, the solve decides); always without the guns switch. */
export function strikeInGunReach(state: GameState, team: number, planetIndex: number, dir: Vec3): boolean {
  if (!gunsFire(state)) return true;
  const planet = state.planets[planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, d = vecNormalize(dir);
  // 69.64: each gun's reach from level ground at its own footing (gunReachPeople)
  return strikeGuns(state, team, planetIndex).some(g => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(g.surfaceDir), d)))) * planet.radius
    <= gunReachPeople(state, planetIndex, undefined, undefined, personSurfaceRadius(planet, vecNormalize(g.surfaceDir), ps)) * person);
}

/** The wind a battery lays its rounds for (a field asked where the round is, as predictShot takes it), or none. */
export type StrikeWind = ((p: Vec3, t: number) => Vec3 | null) | null;

/** The wind the battery knows as it fires at `tick` (the field then, frozen: the gusts in the round's flight are not its to know), on a strike round's share of it; none without the wind switch. */
export function strikeWindAt(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'tick'>>, planetIndex: number, tick: number): StrikeWind {
  if (!state.config.wind) return null;
  const share = windShare({ blast: true, gravity: AIRSTRIKE.fall, wind: ACTION_WIND.shell }, state.config), seconds = tick * PHYS_DT;   // (69.64: a shell's own share)
  return actionWindAccelAt(state as never, planetIndex, share, seconds);   // (69.67: what no place changes taken once — the same field)
}

/**
 * The owner (Oct 20: the salvo "seems to miss cep area by a ton, seemingly by a consistent offset … the wind"): the battery
 * lays a round for the wind it knows — flown through it (predictShot with the field), its miss taken off its aim (by the
 * passes' secant estimate of the landing's slope), again, for AIRSTRIKE.windPasses passes, the best flown kept — then the
 * gunner's correction, `error` of it off (its share, a seeded draw per strike). The round is solved for that aim in still
 * air (its velocity is the still-air solve for the corrected point); where it lands in the wind is near `target`. Returns the flight and the correction's size (world units). Without wind, the plain solve.
 */
export function solveStrikeRoundInWind(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, from: Vec3, target: Vec3, wind: StrikeWind, error: number): { flight: { pos: Vec3; vel: Vec3; seconds: number } | null; correction: number } {
  return layInWind(state, planetIndex, target, wind, error, aim => solveStrikeRound(state, planetIndex, from, aim));
}

/**
 * The wind's correction for any lay (`solve`: the battery's or a gun's, onto an aim) — solveStrikeRoundInWind's passes. A
 * gun's (69.13 (b)) flies them at `step.people` steps asking the field every `step.wind` seconds (the battery's: half a person, a tick).
 */
export function layInWind(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, target: Vec3, wind: StrikeWind, error: number, solve: (aim: Vec3) => { pos: Vec3; vel: Vec3; seconds: number } | null, step?: { people: number; wind: number; fall?: number }): { flight: { pos: Vec3; vel: Vec3; seconds: number } | null; correction: number } {
  const c: LayCtx = { state, planetIndex, target, wind, error, solve, ...(step ? { step } : {}) };
  let p = layStart(target);
  while (p.phase !== 'done') p = layStep(c, p);
  return { flight: p.flight, correction: p.correction };
}

/** 69.67 (§5.32): what a lay's step reads — the state it lays on (that tick's), its target, wind, error and solve — none of it kept between steps. */
export interface LayCtx { state: Pick<GameState, 'planets' | 'config'>; planetIndex: number; target: Vec3; wind: StrikeWind; error: number; solve: (aim: Vec3) => StrikeFlight | null; step?: { people: number; wind: number; fall?: number } }
/** A lay not yet begun. */
export function layStart(target: Vec3): LayPass {
  return { phase: 'plain', k: 0, flight: null, aim: vecNormalize(target), J: [1, 0, 0, 1], u: [0, 0], best: null, last: null, correction: 0 };
}

/**
 * 69.67 (ARCHITECTURE §5.32): one step of layInWind's lay — the plain solve; then a pass (the flight flown to its landing, and
 * the next aim solved); then, with the gunner's error, its correction solved. The same arithmetic in the same order as one call.
 */
export function layStep(c: LayCtx, p: LayPass): LayPass {
  const { state, planetIndex, target, wind, error, solve, step } = c;
  const planet = state.planets[planetIndex];
  if (p.phase === 'done') return p;
  if (p.phase === 'plain') {
    const plain = solve(target);
    if (!wind || !planet || !plain) return { ...p, phase: 'done', flight: plain, correction: 0 };
    return { ...p, phase: 'pass', k: 0, flight: plain };
  }
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, t0 = vecNormalize(target);
  // the aim moved by the miss, on the tangent plane at the target (a few people: flat enough)
  const shift = (aim: Vec3, by: Vec3) => vecNormalize(vecAdd(aim, vecSub(by, vecScale(t0, vecDot(by, t0)))));
  if (p.phase === 'error') {
    const off = solve(shift(t0, vecScale(vecSub(p.aim, t0), 1 + error)));
    return { ...p, phase: 'done', flight: off ?? p.flight };
  }
  const ground = groundTest(planet, ps);
  const lands = (f: { pos: Vec3; vel: Vec3 }): Vec3 | null => {
    const shot = predictShot(f.pos, f.vel, { life: 30, fall: step?.fall ?? AIRSTRIKE.fall, gravityScale: ps.gravityScale, planets: state.planets, blocked: ground, stepLength: (step?.people ?? 0.5) * person, maxSteps: 20000, wind, ...(step ? { windStep: step.wind } : {}) });
    return shot.hit ? vecNormalize(vecSub(shot.at, planet.position)) : null;
  };
  // 69.64 lean 1: the miss is not the aim's offset alone — a nearer aim flies a shorter flight and drifts less — so taking the
  // miss off the aim (a slope of one) overshot and swung (a lay 50 people off in the very field it was laid for). The passes
  // keep a secant estimate of how the landing moves with the aim (Broyden's, on the tangent plane: e1, e2), and the best pass
  // flown is the lay
  const e1 = vecNormalize(vecSub(Math.abs(t0.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 }, vecScale(t0, Math.abs(t0.x) < 0.9 ? t0.x : t0.y)));
  const e2 = { x: t0.y * e1.z - t0.z * e1.y, y: t0.z * e1.x - t0.x * e1.z, z: t0.x * e1.y - t0.y * e1.x };
  const plane = (v: Vec3): [number, number] => [vecDot(v, e1), vecDot(v, e2)];
  let { J, u, aim, best } = p;
  const { k, flight, last } = p;
  // (the loop ended: the best pass flown is the lay — its correction, and the gunner's error of it to solve next)
  const finish = (): LayPass => {
    const a = best ? best.aim : aim, f = best ? best.flight : flight;
    const correction = vecLength(vecSub(a, t0)) * planet.radius;
    return { ...p, phase: error !== 0 && correction > 0 ? 'error' : 'done', aim: a, flight: f, J, u, best, correction };
  };
  const at = lands(flight!);
  if (!at) return finish();
  const m = plane(vecSub(at, t0)), miss = Math.sqrt(m[0] * m[0] + m[1] * m[1]);
  if (!best || miss < best.miss) best = { aim, flight: flight!, miss };
  if (miss * planet.radius <= 0.25 * person || k === AIRSTRIKE.windPasses) return finish();
  if (last) {
    // the secant update: J += (Δm − J·Δu) Δuᵀ / |Δu|²
    const du = [u[0] - last.u[0], u[1] - last.u[1]], dm = [m[0] - last.m[0], m[1] - last.m[1]], dd = du[0] * du[0] + du[1] * du[1];
    if (dd > 0) {
      const r0 = dm[0] - (J[0] * du[0] + J[1] * du[1]), r1 = dm[1] - (J[2] * du[0] + J[3] * du[1]);
      J = [J[0] + (r0 * du[0]) / dd, J[1] + (r0 * du[1]) / dd, J[2] + (r1 * du[0]) / dd, J[3] + (r1 * du[1]) / dd];
    }
  }
  if (!(Math.abs(J[0] * J[3] - J[1] * J[2]) > 1e-6)) J = [1, 0, 0, 1];   // (a degenerate secant: back to a slope of one)
  const d = J[0] * J[3] - J[1] * J[2];
  let s0 = -(J[3] * m[0] - J[1] * m[1]) / d, s1 = -(-J[2] * m[0] + J[0] * m[1]) / d;
  // a step no longer than twice the miss (a secant gone wild on a bad pair does not throw the aim off the map)
  const sl = Math.sqrt(s0 * s0 + s1 * s1);
  if (sl > 2 * miss) { s0 *= (2 * miss) / sl; s1 *= (2 * miss) / sl; }
  const nextLast = { u, m };
  u = [u[0] + s0, u[1] + s1];
  aim = shift(t0, vecAdd(vecScale(e1, u[0]), vecScale(e2, u[1])));
  const next = solve(aim);
  if (!next) return { ...finish(), last: nextLast };
  return { ...p, k: k + 1, J, u, aim, flight: next, best, last: nextLast };
}

/**
 * A side's call at a point: refused with none left (or a point on no planet); else one spent and the strike queued — its
 * circle's centre the point scattered by the fix (a seeded draw: the match seed and the strike's id), its rounds (68.39b) each
 * aimed at a seeded point inside the circle and solved from the off-board battery behind it, fired `staggerSeconds` apart.
 */
export function callAirstrike(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }, tick: number, opts: { rounds?: number; radiusPeople?: number; spread?: boolean } = {}): { state: GameState; strike: AirstrikeInbound } | { refused: string } {
  const a = state.airstrikes;
  if (!a || airstrikesLeft(state, team) <= 0) return { refused: state.config.munitionsSupply && a?.left[team] !== undefined ? 'No shells at the base' : 'No airstrikes left' };   // (69.30 step 3: a whole salvo's shells)
  const planet = state.planets[at.planetIndex];
  if (!planet) return { refused: 'Point at the ground' };
  // 69.13 (b): with the towed guns, the side's guns standing on the planet fire the salvo — none, nothing to fire
  const guns = gunsFire(state) ? strikeGuns(state, team, at.planetIndex) : null;
  if (guns && !guns.length) return { refused: 'No guns ready' };
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const called = vecNormalize(at.surfaceDir);
  // the circle's centre: a seeded draw within the fix's circle (uniform over the disc)
  const { fix, scatterPeople } = airstrikeFix(state, team, at.planetIndex, called, tick);
  const id = a.next, rng = new SeededRandom(`${state.config.seed}-airstrike-${id}`);
  const ang = rng.next() * 2 * Math.PI, r = Math.sqrt(rng.next()) * scatterPeople * person;
  const base = airstrikeHome(state, team);
  const home = base && base.planetIndex === at.planetIndex ? base.surfaceDir : null;
  const line = lineFrom(called, home);
  const side = vecNormalize({ x: called.y * line.z - called.z * line.y, y: called.z * line.x - called.x * line.z, z: called.x * line.y - called.y * line.x });
  const off = vecAdd(vecScale(line, dcos(ang)), vecScale(side, dsin(ang)));
  const aim = r > 0 ? along(called, vecNormalize(off), r, planet.radius) : called;
  // 68.39b: its rounds — each at a seeded point in the circle, from the battery behind it on the line from home
  const n = opts.rounds !== undefined ? Math.max(1, Math.min(12, Math.floor(opts.rounds))) : salvoSize(state);
  // 69.64 (288): a seen fix lays the rounds in a tighter circle (the match's or the call's own radius over either)
  const radius = Math.max(0, opts.radiusPeople ?? state.config.airstrikeRadius ?? (fix === 'seen' ? AIRSTRIKE.seenRadiusPeople : AIRSTRIKE.radiusPeople));
  const spread = new SeededRandom(`${state.config.seed}-airstrike-spread-${id}`);   // (69.64: each round's dispersion, a draw of its own)
  const toward = lineFrom(aim, home), battery = along(aim, toward, -AIRSTRIKE.batteryPeople * person, planet.radius);
  const across = vecNormalize({ x: aim.y * toward.z - aim.z * toward.y, y: aim.z * toward.x - aim.x * toward.z, z: aim.x * toward.y - aim.y * toward.x });
  const first = tick + Math.round(AIRSTRIKE.prepSeconds / PHYS_DT), stagger = Math.round(AIRSTRIKE.staggerSeconds / PHYS_DT);
  // with the wind on, its gunner's correction's error for this strike (a draw of its own: the still-air strike's draws are as
  // before); each round laid for the wind as it fires (below)
  const error = state.config.wind ? (new SeededRandom(`${state.config.seed}-airstrike-wind-${id}`).next() * 2 - 1) * AIRSTRIKE.windError : 0;
  // its rounds' aims, each drawn here in turn (the draws as they were: a round's lay draws none), queued to be laid —
  // 69.67 (ARCHITECTURE §5.32): with `spread` (the Match's calls) a step a tick from this one, on each tick's ground; without,
  // here and now (the same steps). A gun the call already knows cannot reach (gunReaches, level ground) is left out when spread
  const queue: StrikeLaying['queue'] = [];
  for (let k = 0; k < (guns ? Math.min(n, guns.length) : n); k++) {
    const ra = rng.next() * 2 * Math.PI, rr = Math.sqrt(rng.next()) * radius * person;
    const dirK = vecNormalize(vecAdd(vecScale(toward, dcos(ra)), vecScale(across, dsin(ra))));
    const aimK = rr > 0 ? along(aim, dirK, rr, planet.radius) : aim;
    if (guns) {
      const gun = guns[k], laidFor = dispersed(planet, aimK, gun.surfaceDir, spread);   // (69.64: its dispersion by its range from the gun)
      if (!opts.spread || gunReaches(state, at.planetIndex, gun, laidFor)) queue.push({ k, aim: aimK, laidFor, gun: gun.id });
      continue;
    }
    queue.push({ k, aim: aimK, laidFor: dispersed(planet, aimK, home, spread) });   // (69.64: the off-board battery's dispersion by its range from home)
  }
  if (guns && !queue.length) return { refused: 'Out of range' };   // 69.13 (b): no gun reaches (nothing spent)
  const laying: StrikeLaying = { queue, first, stagger, error, drift: 0, ...(guns ? {} : { battery }) };
  const strike0: AirstrikeInbound = { id, team, planetIndex: at.planetIndex, aim, called, calledTick: tick, impactTick: first, fix, scatterPeople, radiusPeople: radius, rounds: [], laying };
  if (opts.spread) {
    // spent now — a second call this tick sees it gone (the commander's pairs) — and handed back at the end for rounds that never laid
    if (state.config.munitionsSupply) {
      const structures = spendBaseShells(state.structures, team, queue.length), shells: Array<[number, number]> = [];
      structures.forEach((s, i) => { const was = state.structures[i]; if (s !== was) shells.push([s.id, (was.supply?.ammo?.lightShell ?? 0) - (s.supply?.ammo?.lightShell ?? 0)]); });
      const spent = { ...strike0, laying: { ...laying, spent: { shells } } };
      return { state: { ...state, structures, airstrikes: { ...a, inbound: [...a.inbound, spent], next: id + 1 } }, strike: spent };
    }
    const spent = { ...strike0, laying: { ...laying, spent: { strike: true as const } } };
    return { state: { ...state, airstrikes: { ...a, left: { ...a.left, [team]: (a.left[team] ?? 0) - 1 }, inbound: [...a.inbound, spent], next: id + 1 } }, strike: spent };
  }
  let strike: AirstrikeInbound | null = strike0;
  while (strike?.laying) strike = layStrike(state, strike).strike;
  if (!strike) return { refused: 'Out of range' };   // 69.77: no round laid (layStrike drops such a strike; nothing spent)
  const rounds = strike.rounds;
  if (guns && !rounds.length) return { refused: 'Out of range' };   // 69.13 (b): no gun reaches (nothing spent)
  // 69.30 step 3: with the munitions supply, the rounds fired come off the base's light shells (the count stands)
  if (state.config.munitionsSupply) return { state: { ...state, structures: spendBaseShells(state.structures, team, rounds.length), airstrikes: { ...a, inbound: [...a.inbound, strike], next: id + 1 } }, strike };
  return { state: { ...state, airstrikes: { ...a, left: { ...a.left, [team]: (a.left[team] ?? 0) - 1 }, inbound: [...a.inbound, strike], next: id + 1 } }, strike };
}

/** 69.67: of the shells a call took (by crate, in the order taken), `count` to hand back — from the last crate taken from first. */
function handBack(taken: ReadonlyArray<[number, number]>, count: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let left = count;
  for (let i = taken.length - 1; i >= 0 && left > 0; i--) { const g = Math.min(left, taken[i][1]); if (g > 0) { out.push([taken[i][0], g]); left -= g; } }
  return out;
}

/** 69.67: the shells handed back to their crates (a crate since gone keeps none). */
export function returnShells(structures: readonly StructureState[], shells: ReadonlyArray<[number, number]>): StructureState[] {
  const back = new Map(shells);
  return structures.map(s => { const n = back.get(s.id); return n && s.supply?.ammo ? { ...s, supply: { ...s.supply, ammo: { ...s.supply.ammo, lightShell: (s.supply.ammo.lightShell ?? 0) + n } } } : s; });
}

/** 69.67: whether a gun reaches a point from level ground at its own footing (strikeInGunReach's test, one gun). */
function gunReaches(state: GameState, planetIndex: number, g: Pick<StructureState, 'surfaceDir'>, dir: Vec3): boolean {
  const planet = state.planets[planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, gd = vecNormalize(g.surfaceDir);
  return dacos(Math.max(-1, Math.min(1, vecDot(gd, vecNormalize(dir))))) * planet.radius <= gunReachPeople(state, planetIndex, undefined, undefined, personSurfaceRadius(planet, gd, ps)) * person;
}

/**
 * 69.67 (ARCHITECTURE §5.32): one step of a strike's lays, on `state` (the tick's): the next round begun (its fire tick, its
 * charge, its plain solve), or its lay's next pass, or its correction — a round laid joins `rounds`, one that cannot lay (its
 * gun gone, out of reach) is passed over. The last laid, the strike is whole: its impact tick, its drift circle, no `laying`.
 * `refund`: what to hand back of what the call spent (the shells of rounds never laid; the strike itself, a guns' strike none of
 * whose rounds laid — nothing is fired; a mortar's rounds never laid, to its tube), and `strike` null for such a strike (it is
 * dropped). A mortar mission (`laying.mortar`) lays from its tube as callMortar did at once.
 */
export function layStrike(state: GameState, s: AirstrikeInbound): { strike: AirstrikeInbound | null; refund?: { shells?: Array<[number, number]>; strike?: true; mortar?: { gun: number; rounds: number } } } {
  const L = s.laying!;
  const planet = state.planets[s.planetIndex];
  let cur = L.current, rounds = s.rounds, near = L.near, drift = L.drift, queue = L.queue;
  if (!cur && queue.length && planet) {
    const q = queue[0];
    queue = queue.slice(1);
    const gun = q.gun !== undefined ? state.structures.find(x => x.id === q.gun && x.health > 0) : undefined;
    if (q.gun === undefined || gun) {
      const fireTick = gun ? L.first + rounds.length * L.stagger : L.first + q.k * L.stagger;   // (the guns that fire in turn; the battery's at its place)
      cur = { ...q, fireTick, ...(gun && !L.mortar ? { charge: gunCharge(state, s.planetIndex, gun, q.laidFor) } : {}), pass: layStart(q.laidFor) };   // (69.64: the lowest charge that reaches; a mortar has one)
    }
  }
  if (cur && planet) {
    const cls = GUN_CLASSES[TOWED_GUNS.gunClass], charges = gunCharges(state, s.planetIndex), gun = cur.gun !== undefined ? state.structures.find(x => x.id === cur!.gun && x.health > 0) : undefined;
    let pass: LayPass | null = null;
    if (L.mortar) {
      // 69.67, a mortar mission (69.23): its tube's lay from where it stood at the call — the high solution at the mortar's
      // speed, steep enough to clear what is near (MORTAR.minElevation) — at the guns' step through the wind, while it stands
      const m = L.mortar;
      if (gun) pass = layStep({ state, planetIndex: s.planetIndex, target: cur.laidFor, wind: strikeWindAt(state, s.planetIndex, cur.fireTick), error: L.error, solve: q => {
        const f = solveLay(state, s.planetIndex, m.from, q, m.speed, { solution: 'high' });
        return f && f.elevation >= MORTAR.minElevation ? f : null;
      }, step: { people: TOWED_GUNS.layStepPeople, wind: TOWED_GUNS.layWindStep } }, cur.pass);
    } else if (cur.gun === undefined) {
      const from = L.battery!;
      pass = layStep({ state, planetIndex: s.planetIndex, target: cur.laidFor, wind: strikeWindAt(state, s.planetIndex, cur.fireTick), error: L.error, solve: q => solveStrikeRound(state, s.planetIndex, from, q) }, cur.pass);   // (laid as it fires: the wind then)
    } else if (gun) {
      // 69.13 (b): gun k lays its round from where it stands — the high solution at its class's speed (DECISIONS 232), a higher charge if its lay fails
      const charge = cur.charge ?? 0;
      pass = layStep({ state, planetIndex: s.planetIndex, target: cur.laidFor, wind: strikeWindAt(state, s.planetIndex, cur.fireTick), error: L.error, solve: q => {
        for (let c = charge; c < charges.length; c++) {
          const speed = charges[c], f = solveLay(state, s.planetIndex, gun.surfaceDir, q, speed, { ...(near?.speed === speed ? { near: near.elevation } : {}), fall: cls.fall });
          if (f) { near = { speed, elevation: f.elevation }; return f; }
        }
        return null;
      }, step: { people: layStepPeople(charges[charge]), wind: TOWED_GUNS.layWindStep, fall: cls.fall } }, cur.pass);
    }
    if (pass && pass.phase !== 'done') cur = { ...cur, pass };
    else {
      const flight = pass?.flight ?? null;
      if (flight) {
        drift = Math.max(drift, pass!.correction);
        const ticks = Math.round(flight.seconds / PHYS_DT);
        rounds = [...rounds, cur.gun !== undefined
          ? { aim: cur.aim, pos: flight.pos, vel: flight.vel, fireTick: cur.fireTick, landTick: cur.fireTick + ticks, gun: cur.gun, ...(!L.mortar && cls.fall !== AIRSTRIKE.fall ? { fall: cls.fall } : {}) }
          : { aim: cur.aim, pos: flight.pos, vel: flight.vel, fireTick: cur.fireTick, landTick: cur.fireTick + ticks }];
      }
      cur = undefined;
    }
  }
  if (cur || (queue.length && planet)) {
    const { current: _c, ...rest } = L;
    void _c;
    return { strike: { ...s, rounds, laying: { ...rest, queue, ...(cur ? { current: cur } : {}), drift, ...(near ? { near } : {}) } } };
  }
  // the last laid: the strike whole
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const { laying: _l, ...whole } = s;
  void _l;
  const guns = L.battery === undefined;
  const refundShells = L.spent?.shells ? handBack(L.spent.shells, L.spent.shells.reduce((t, x) => t + x[1], 0) - rounds.length) : undefined;
  const refundRounds = L.mortar && L.spent?.rounds ? { gun: L.mortar.gun, rounds: L.spent.rounds - rounds.length } : undefined;   // (a mortar's: back to its tube)
  if (guns && !rounds.length) return { strike: null, refund: { ...(L.spent?.shells ? { shells: L.spent.shells } : {}), ...(L.spent?.strike ? { strike: true as const } : {}), ...(refundRounds ? { mortar: refundRounds } : {}) } };
  const driftPeople = state.config.wind ? (drift * AIRSTRIKE.windSpread) / person : 0;
  const strike: AirstrikeInbound = { ...whole, rounds, impactTick: rounds.length ? Math.max(...rounds.map(q => q.landTick)) : L.first, ...(driftPeople > 0 ? { driftPeople } : {}), ...(s.own ? { ownLaid: rounds.length } : {}) };
  return { strike, ...(refundShells?.length || refundRounds?.rounds ? { refund: { ...(refundShells?.length ? { shells: refundShells } : {}), ...(refundRounds?.rounds ? { mortar: refundRounds } : {}) } } : {}) };
}

/**
 * 69.64 (DECISIONS 288): a round's own dispersion — where it is laid for, off its aim by a circular error probable of
 * AIRSTRIKE.dispersion of its range from `from` (the gun, or the side's home for the off-board battery; none without one):
 * a Rayleigh draw (half the rounds within the CEP) at a seeded bearing. Returns the aim itself at no range.
 */
export function dispersed(planet: PlanetState, aim: Vec3, from: Vec3 | null, rng: SeededRandom, share: number = AIRSTRIKE.dispersion): Vec3 {
  const u = rng.next(), b = rng.next() * 2 * Math.PI;   // (both drawn always: the draws' order does not hang on the range)
  if (!from || (from.x === aim.x && from.y === aim.y && from.z === aim.z)) return aim;   // (no range: the aim itself, exactly — a point equal by value has none, whatever its self-dot rounds to)
  const a = vecNormalize(aim), range = dacos(Math.max(-1, Math.min(1, vecDot(a, vecNormalize(from))))) * planet.radius;
  const sigma = (share * range) / 1.1774100225154747, r = sigma * Math.sqrt(-2 * dlog(Math.max(1e-6, 1 - u)));   // (CEP = σ √(2 ln 2))
  if (!(r > 0)) return aim;
  const e1 = vecNormalize(vecSub(Math.abs(a.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 }, vecScale(a, Math.abs(a.x) < 0.9 ? a.x : a.y)));
  const e2 = { x: a.y * e1.z - a.z * e1.y, y: a.z * e1.x - a.x * e1.z, z: a.x * e1.y - a.y * e1.x };
  return along(a, vecNormalize(vecAdd(vecScale(e1, dcos(b)), vecScale(e2, dsin(b)))), r, planet.radius);
}

/** 68.39b: a strike's rounds' aims as ground points (its inbound warning's, the bots' and the page's). */
export function strikePoints(state: GameState, s: AirstrikeInbound): Vec3[] {
  const planet = state.planets[s.planetIndex], ps = personSetOf(state);
  return planet ? s.rounds.map(q => personPointOn(planet, q.aim, ps, 0)) : [];
}

/**
 * One tick: each strike's rounds due are fired (`fire`: the Match makes each a round in flight — the bullets' step flies it, and
 * it bursts by its class where it lands); a strike stays until its last round's solved landing (the side's mark stands till
 * then). A call not yet announced is announced, and within AIRSTRIKE.warnSeconds of its first round's landing it is warned
 * (both once).
 */
export function stepAirstrikes(state: GameState, tick: number): { state: GameState; fire: Array<{ strike: AirstrikeInbound; round: StrikeRound }>; announced: AirstrikeInbound[]; warned: AirstrikeInbound[] } {
  const a = state.airstrikes;
  if (!a?.inbound.length) return { state, fire: [], announced: [], warned: [] };
  const fire: Array<{ strike: AirstrikeInbound; round: StrikeRound }> = [], announced: AirstrikeInbound[] = [], warned: AirstrikeInbound[] = [];
  const warnTicks = Math.round(AIRSTRIKE.warnSeconds / PHYS_DT);
  const inbound: AirstrikeInbound[] = [];
  let structures = state.structures, held = a.left;
  for (const s0 of a.inbound) {
    let s = s0;
    // 69.67 (ARCHITECTURE §5.32): a strike still being laid takes its step on this tick's ground — announced (its event's impact
    // tick, its drift circle), warned and fired only once whole; what its call spent for rounds that never laid handed back
    if (s.laying) {
      const r = layStrike(state, s);
      if (r.refund?.shells) structures = returnShells(structures, r.refund.shells);
      if (r.refund?.strike) held = { ...held, [s.team]: (held[s.team] ?? 0) + 1 };
      if (r.refund?.mortar) { const { gun, rounds } = r.refund.mortar; structures = structures.map(x => (x.id === gun && x.mortar ? { ...x, mortar: { ...x.mortar, rounds: x.mortar.rounds + rounds } } : x)); }
      if (!r.strike) continue;
      s = r.strike;
      if (s.laying) { inbound.push(s); continue; }
    }
    if (!s.announced) { s = { ...s, announced: true }; announced.push(s); }
    const firstLand = s.rounds.length ? Math.min(...s.rounds.map(q => q.landTick)) : s.impactTick;
    if (!s.warned && tick >= firstLand - warnTicks) { s = { ...s, warned: true, warnPoints: strikePoints(state, s) }; warned.push(s); }
    const left = s.rounds.filter(q => q.fireTick > tick);
    for (const q of s.rounds) if (q.fireTick <= tick) fire.push({ strike: s, round: q });
    if (left.length !== s.rounds.length) s = { ...s, rounds: left };
    if (left.length || tick < s.impactTick) inbound.push(s);
  }
  return { state: { ...state, ...(structures !== state.structures ? { structures } : {}), airstrikes: { ...a, inbound, ...(held !== a.left ? { left: held } : {}) } }, fire, announced, warned };
}

/** The seat that owns a side's airstrike bursts (no unit: the commander's, below every unit id). */
export const airstrikeOwner = (team: number): number => -1 - team;

/**
 * How many of `team`'s own people stand within a strike's reach of a point: the stick's half-length, its burst's damage
 * circle and the guess's scatter (the widest it may land) — the radial's warning (0: none).
 */
export function friendliesInStrikeReach(state: GameState, team: number, planetIndex: number, dir: Vec3): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const reach = ((state.config.airstrikeRadius ?? AIRSTRIKE.radiusPeople) + EXPLOSIONS[AIRSTRIKE.explosion].blast!.radiusPeople + AIRSTRIKE.scatterPeople.guess) * person;   // (68.39b: the circle, a burst and a guess's scatter)
  const d = vecNormalize(dir);
  return state.players.filter(p => p.alive && p.teamIndex === team && p.planetIndex === planetIndex
    && dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(p.surfaceDir), d)))) * planet.radius <= reach).length;
}
